import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";
import { usePaywallList } from "@circle-react/hooks/paywalls/usePaywallList";
import { PAYWALL_STATUS } from "@circle-react/types/Paywall";
import { paywallStatusesDefault } from "@circle-react-shared/Paywalls/Modals/CreatePaywallSubscriptionModal/Form/hooks/usePaywallForm";

interface BulkImportTrialPaywallSubscriptionFormContextValue {
  hideNotifyMemberForm?: boolean;
  isPaywallLoading: boolean;
  paywallCouponCode: string;
  paywallId: string;
  paywallPriceId: string;
  paywallTrialDays: string;
  setPaywallCouponCode: (value: string) => void;
  setPaywallId: (value: string) => void;
  setPaywallPriceId: (value: string) => void;
  setPaywallTrialDays: (value: string) => void;
  skipDateValidations: boolean;
}

interface BulkImportTrialPaywallSubscriptionFormContextProviderProps {
  children: ReactNode;
  communityMemberId?: number;
  hideNotifyMemberForm?: boolean;
  paywallCouponCode?: string;
  paywallId?: number;
  paywallPriceId?: number;
  paywallTrialDays?: number;
  skipDateValidations?: boolean;
}

const BulkImportTrialPaywallSubscriptionFormContext =
  createContext<BulkImportTrialPaywallSubscriptionFormContextValue>(
    {} as BulkImportTrialPaywallSubscriptionFormContextValue,
  );
BulkImportTrialPaywallSubscriptionFormContext.displayName =
  "BulkImportTrialPaywallSubscriptionFormContext";

export const BulkImportTrialPaywallSubscriptionFormContextProvider = ({
  paywallId: paywallIdParam,
  paywallPriceId: paywallPriceIdParam,
  paywallTrialDays: paywallTrialDaysParam,
  paywallCouponCode: paywallCouponCodeParam,
  skipDateValidations: skipDateValidationsParam,
  hideNotifyMemberForm,
  children,
}: BulkImportTrialPaywallSubscriptionFormContextProviderProps) => {
  const [paywallId, setPaywallId] = useState<string>(
    paywallIdParam?.toString() ?? "",
  );
  const [paywallPriceId, setPaywallPriceId] = useState<string>(
    paywallPriceIdParam?.toString() ?? "",
  );
  const [paywallTrialDays, setPaywallTrialDays] = useState<string>(
    paywallTrialDaysParam?.toString() ?? "",
  );
  const [paywallCouponCode, setPaywallCouponCode] = useState<string>(
    paywallCouponCodeParam ?? "",
  );
  const { paywallsListQuery } = usePaywallList({
    communityMemberId: null,
    status: skipDateValidationsParam
      ? paywallStatusesDefault
      : [PAYWALL_STATUS.ACTIVE],
  });
  const isPaywallLoading = paywallsListQuery.isLoading;

  const valueToStore: BulkImportTrialPaywallSubscriptionFormContextValue = {
    paywallId,
    setPaywallId,
    paywallPriceId,
    setPaywallPriceId,
    paywallTrialDays,
    setPaywallTrialDays,
    paywallCouponCode,
    setPaywallCouponCode,
    isPaywallLoading,
    skipDateValidations: Boolean(skipDateValidationsParam),
    hideNotifyMemberForm,
  };

  return (
    <BulkImportTrialPaywallSubscriptionFormContext.Provider
      value={valueToStore}
    >
      {children}
    </BulkImportTrialPaywallSubscriptionFormContext.Provider>
  );
};

export const useBulkImportTrialPaywallSubscriptionFormContext =
  (): BulkImportTrialPaywallSubscriptionFormContextValue =>
    useContext(BulkImportTrialPaywallSubscriptionFormContext);
